import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import { useTranslation } from 'react-i18next'
import { tokens } from '../theme'

// Every interruption in the scan flow, in one shape: what the piece is, what the
// system decided about it, and what the clerk may do next.
//
// It replaces four things declared in InventoryCheck's render body -- ScannedProduct,
// ActionArea, AlreadyScanned and ProductHasMultiple -- which were one component
// pretending to be four. Living at module scope is not a style preference: the page
// invokes its subviews as Header() rather than <Header/> precisely because a component
// redeclared on every render remounts and drops the barcode field's focus. A real
// component declared out here can be rendered as JSX safely.
//
// The band's colour is STATUS. Green appears here only on `counted`, where it means
// "recorded", never "press me" -- the rule that the old Add +1 button broke.

const IMAGE_SIZE = 120

// Informational, not alarming: a repeat scan is a normal event. Amber is reserved
// for the one verdict that means "you are counting the wrong thing".
const NEUTRAL = { fill: tokens.accentSoft, ink: tokens.text }

export const VERDICT_TONES = {
    counted: { fill: tokens.successSoft, ink: tokens.success },
    already: NEUTRAL,
    countMore: NEUTRAL,
    multiple: NEUTRAL,
    unscanned: NEUTRAL,
    outOfScope: { fill: tokens.warningSoft, ink: tokens.warning }
}

function ProductIdentity(props) {
    const { product, imageLoading, onImageLoad, onImageClick } = props
    const { t } = useTranslation()
    const { barcode, metal, price, imageUrl } = product ?? {}

    // == null, never a truthy test: 0 is a real price.
    const facts = [metal, price == null ? null : `$${price}`].filter(Boolean).join(' · ')

    return (
        <Box sx={{ display: 'flex', gap: 2, p: 2, alignItems: 'center' }}>
            <Box
                sx={{
                    width: IMAGE_SIZE,
                    height: IMAGE_SIZE,
                    flex: 'none',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    bgcolor: tokens.ground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {imageUrl ? (<>
                    <img
                        src={imageUrl}
                        alt='product'
                        onLoad={onImageLoad}
                        onError={onImageLoad}
                        onClick={onImageClick}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer',
                            display: imageLoading ? 'none' : 'block'
                        }}
                    />
                    {imageLoading && <Skeleton variant='rectangular' width={IMAGE_SIZE} height={IMAGE_SIZE} />}
                </>) : (
                    <Typography variant='caption' color='text.secondary'>{t('inventoryCheck.noPhoto')}</Typography>
                )}
            </Box>

            <Box sx={{ minWidth: 0 }}>
                {/* The barcode leads. It is the only field that is always present and the
                    only one that answers "is this the piece in my hand". */}
                <Typography variant='h6' sx={{ fontVariantNumeric: 'tabular-nums' }}>#{barcode}</Typography>
                {facts && <Typography variant='body2' color='text.secondary'>{facts}</Typography>}
            </Box>
        </Box>
    )
}

export default function ScanVerdict(props) {
    const {
        verdict,
        product,
        heading,
        detail,
        imageLoading,
        onImageLoad,
        onImageClick,
        actions,
        children
    } = props

    // Fall back to neutral, never to `counted`: an unrecognised verdict is a
    // programming error, and asserting "recorded" (green) for one would tell a
    // clerk a piece was counted when the system determined nothing at all.
    const tone = VERDICT_TONES[verdict] ?? NEUTRAL

    return (
        <Box
            data-testid='scan-verdict'
            data-verdict={verdict}
            sx={{
                width: '100%',
                maxWidth: '480px',
                borderRadius: '12px',
                border: `1px solid ${tokens.border}`,
                bgcolor: tokens.surface,
                overflow: 'hidden'
            }}
        >
            <ProductIdentity
                product={product}
                imageLoading={imageLoading}
                onImageLoad={onImageLoad}
                onImageClick={onImageClick}
            />

            <Box sx={{ bgcolor: tone.fill, px: 2, py: 1.5 }}>
                <Typography variant='subtitle1' sx={{ color: tone.ink, fontWeight: 600 }}>
                    {heading}
                </Typography>
                {detail && <Typography variant='body2' sx={{ mt: 0.5 }}>{detail}</Typography>}
            </Box>

            {(children || actions) && (
                <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {children}
                    {actions}
                </Box>
            )}
        </Box>
    )
}
